import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Plus, Check, Users, Pencil, MoreHorizontal } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { useNotification } from '@/hooks/useNotification';
import {
  sortGroupsListByName,
  capitalize,
  currentWorkspaceHasExpired,
  userIsRootGroupCreator,
} from '../../../../util/UtilFunctions';
import { cutItemTitle } from '../../../../util/FitTextIntoWidth';
import { getPackageType } from '../../../../util/subscriptionDetails';
import { hasExpired } from '../../../../util/subscriptionDetails';

import SetPictureModal from '../../../../components/modals/setPictureModal/SetPictureModal';
import CreateWorkspaceModal from '../../../../components/modals/createWorkspaceModal/CreateWorkspaceModal';
import WorkspaceParticipiantsModal from '../../../../components/modals/workspaceParticipiantsModal/WorkspaceParticipiantsModal';
import ExitWorkspaceModal from '../../../../components/modals/exitWorkspaceModal/ExitWorkspaceModal';
import RenameModal from '../../../../components/modals/renameModal/renameModal';
import DeleteItemModal from '../../../../components/modals/deleteItemModal/DeleteItemModal';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Group = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UserInfo = any;

interface AccountSettingsWorkspaceProps {
  groupsList: Group[];
  currentUserGroup: string;
  currentUserInfo: UserInfo;
  updateIsQuerying: (q: boolean) => void;
  refreshList: (...args: unknown[]) => void;
  initiateSession: () => void;
  history?: { push: (url: string) => void };
  getWorkspacePeopleList?: () => void;
  updateCurrentUserInfo?: () => void;
}

// ── Main Component ──────────────────────────────────────────────────

export default function AccountSettingsWorkspace({
  groupsList,
  currentUserGroup,
  currentUserInfo,
  updateIsQuerying,
  refreshList,
  initiateSession,
  getWorkspacePeopleList,
  updateCurrentUserInfo,
}: AccountSettingsWorkspaceProps) {
  const navigate = useNavigate();
  const { notify } = useNotification();

  const [openSections, setOpenSections] = useState<number[]>([1, 2, 3, 4, 5]);
  const [modalOpen, setModalOpen] = useState<string | undefined>();
  const [modalParams, setModalParams] = useState<unknown[] | undefined>();

  // Adjust default open sections based on workspace hierarchy
  useEffect(() => {
    const current = groupsList.find((g: Group) => g.id.S === currentUserGroup);
    if (!current) return;
    const isSubGroup = current.parent.S !== 'root';
    const parentKnown = groupsList.some((g: Group) => g.id.S === current.parent.S);
    if (isSubGroup && parentKnown) setOpenSections([1, 2]);
    else if (isSubGroup && !parentKnown) setOpenSections([1, 3]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSection = (idx: number) => {
    if (currentWorkspaceHasExpired(groupsList) && idx === 2) {
      notify({ type: 'info', message: 'Free trial period has ended, please upgrade your account' });
      return;
    }
    setOpenSections((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx],
    );
  };

  const handleCreateWorkspace = (useCase: string) => {
    if (currentWorkspaceHasExpired(groupsList)) {
      notify({ type: 'info', message: 'Free trial period has ended, please upgrade your account' });
      return;
    }
    setModalParams([{ useCase }]);
    setModalOpen('createWorkspace');
  };

  // ── Workspace list extraction ──────────────────────────────────

  const sorted = sortGroupsListByName([...groupsList]);
  const rootGroups = sorted.filter((g: Group) => g.parent.S === 'root');

  const currentWs = groupsList.find((g: Group) => g.id.S === currentUserGroup);
  const isRootSelected = currentWs?.parent.S === 'root';

  // Sub-groups of the current root workspace
  const rootSubGroups = (() => {
    if (!currentWs) return [];
    if (isRootSelected) return sorted.filter((g: Group) => g.parent.S === currentWs.id.S);
    const parent = groupsList.find((g: Group) => g.id.S === currentWs.parent.S);
    if (parent) return sorted.filter((g: Group) => g.parent.S === parent.id.S);
    return [];
  })();

  // Groups with unknown parents (invited workspaces from other orgs)
  const otherGroups = (() => {
    const nonRoot = groupsList.filter((g: Group) => g.parent.S !== 'root');
    return nonRoot.filter((g: Group) => !rootGroups.some((r: Group) => r.id.S === g.parent.S));
  })();

  const subGroupsTitle = (() => {
    if (!currentWs) return '';
    if (isRootSelected) return `${cutItemTitle(capitalize(currentWs.title.S), 200)} workspaces (${rootSubGroups.length})`;
    const parent = rootGroups.find((g: Group) => g.id.S === currentWs.parent.S);
    if (parent) return `${cutItemTitle(capitalize(parent.title.S), 200)} workspaces (${rootSubGroups.length})`;
    return '';
  })();

  const userIsCreator = userIsRootGroupCreator(currentUserInfo, groupsList);
  const canAddWorkspaces = (() => {
    if (!currentWs) return false;
    const userWsObj = currentUserInfo.groups?.find((g: Group) => g.id.S === currentWs.id.S);
    if (!userWsObj) return false;
    const isAdminOrPro = userWsObj.role.S === 'admin' || userWsObj.role.S === 'pro';
    return currentWs.parent.S === 'root' && isAdminOrPro;
  })();

  const roleInCurrentWorkspace = currentUserInfo.groups?.find(
    (g: Group) => g.id.S === currentUserGroup,
  )?.role.S ?? 'user';

  return (
    <div className="space-y-6">
      {/* Create own workspace prompt (for non-creators) */}
      {!userIsCreator && (
        <div className="rounded-lg border border-border p-4">
          <h4 className="text-sm font-semibold text-foreground">Create your own yemma</h4>
          <Button className="mt-3" onClick={() => handleCreateWorkspace('root')}>Create</Button>
        </div>
      )}

      {/* Root workspace administration */}
      {rootGroups.length > 0 && (
        <CollapsibleSection title="Workspace control" open={openSections.includes(1)} onToggle={() => toggleSection(1)}>
          <ul className="space-y-2">
            {rootGroups.map((group: Group) => (
              <AdminListItem
                key={group.id.S}
                group={group}
                isActive={group.id.S === currentUserGroup}
                onActivate={() => { localStorage.setItem('currentUserGroup', group.id.S); initiateSession(); }}
                onRename={() => { setModalParams([{ workspace: group }]); setModalOpen('renameWorkspace'); }}
                onPeople={() => { setModalParams([{ groupId: group.id.S }]); setModalOpen('workspacePeopleList'); }}
                onExpiredClick={() => navigate('/in/account/billing')}
              />
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {/* Sub-workspaces of current root */}
      {rootSubGroups.length > 0 && (
        <CollapsibleSection
          title={subGroupsTitle}
          open={openSections.includes(2)}
          onToggle={() => toggleSection(2)}
          disabled={currentWorkspaceHasExpired(groupsList)}
        >
          <WorkspaceList
            groups={rootSubGroups}
            currentUserGroup={currentUserGroup}
            currentUserInfo={currentUserInfo}
            initiateSession={initiateSession}
            onModal={(type, params) => { setModalParams(params); setModalOpen(type); }}
          />
        </CollapsibleSection>
      )}

      {/* Other workspaces */}
      {otherGroups.length > 0 && (
        <CollapsibleSection title={`Other workspaces (${otherGroups.length})`} open={openSections.includes(3)} onToggle={() => toggleSection(3)}>
          <WorkspaceList
            groups={otherGroups}
            currentUserGroup={currentUserGroup}
            currentUserInfo={currentUserInfo}
            initiateSession={initiateSession}
            onModal={(type, params) => { setModalParams(params); setModalOpen(type); }}
          />
        </CollapsibleSection>
      )}

      {/* Add workspace button */}
      {canAddWorkspaces && (
        <button
          type="button"
          onClick={() => handleCreateWorkspace(currentUserGroup)}
          className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            currentWorkspaceHasExpired(groupsList)
              ? 'cursor-not-allowed text-muted-foreground'
              : 'text-primary hover:bg-primary-muted'
          }`}
        >
          <Plus className="size-4" /> Add workspace
        </button>
      )}

      {/* Modals */}
      <WorkspaceModals
        modalOpen={modalOpen}
        modalParams={modalParams}
        onClose={() => setModalOpen(undefined)}
        updateIsQuerying={updateIsQuerying}
        currentUserInfo={currentUserInfo}
        refreshList={refreshList}
        currentUserGroup={currentUserGroup}
        groupsList={groupsList}
        getWorkspacePeopleList={getWorkspacePeopleList}
        updateCurrentUserInfo={updateCurrentUserInfo}
        roleInCurrentWorkspace={roleInCurrentWorkspace}
        packageType={getPackageType(currentUserInfo, groupsList)}
      />
    </div>
  );
}

// ── Collapsible Section ─────────────────────────────────────────────

function CollapsibleSection({ title, open, onToggle, disabled, children }: {
  title: string;
  open: boolean;
  onToggle: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const Icon = open ? ChevronDown : ChevronRight;
  return (
    <div className="rounded-lg border border-border">
      <button
        type="button"
        onClick={onToggle}
        className={`flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-semibold transition-colors ${
          disabled ? 'cursor-not-allowed text-muted-foreground' : 'text-foreground hover:bg-secondary'
        }`}
      >
        <Icon className="size-4 shrink-0" />
        {title}
      </button>
      {open && <div className="border-t border-border-light px-4 py-3">{children}</div>}
    </div>
  );
}

// ── Admin List Item (root workspaces) ───────────────────────────────

function AdminListItem({ group, isActive, onActivate, onRename, onPeople, onExpiredClick }: {
  group: Group;
  isActive: boolean;
  onActivate: () => void;
  onRename: () => void;
  onPeople: () => void;
  onExpiredClick: () => void;
}) {
  const expired = hasExpired(group);
  return (
    <li className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-secondary">
      <button
        type="button"
        onClick={isActive ? undefined : onActivate}
        className={`flex size-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
          isActive ? 'border-primary bg-primary text-white' : 'border-border hover:border-primary'
        }`}
        title={isActive ? 'Active' : 'Activate'}
      >
        {isActive && <Check className="size-3.5" />}
      </button>

      <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
        {isActive ? (
          <button type="button" onClick={onRename} className="hover:text-primary hover:underline">
            {cutItemTitle(capitalize(group.title.S), 200)}
          </button>
        ) : (
          cutItemTitle(capitalize(group.title.S), 200)
        )}
      </span>

      {expired && (
        <Button size="sm" variant="destructive" onClick={onExpiredClick}>Expired</Button>
      )}

      <button type="button" onClick={onPeople} className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-muted-foreground" title="Participants">
        <Users className="size-4" />
      </button>
    </li>
  );
}

// ── Workspace List (sub-workspaces) ─────────────────────────────────

function WorkspaceList({ groups, currentUserGroup, currentUserInfo, initiateSession, onModal }: {
  groups: Group[];
  currentUserGroup: string;
  currentUserInfo: UserInfo;
  initiateSession: () => void;
  onModal: (type: string, params?: unknown[]) => void;
}) {
  return (
    <ul className="space-y-1">
      {groups.map((group: Group) => (
        <WorkspaceListItem
          key={group.id.S}
          group={group}
          isActive={group.id.S === currentUserGroup}
          currentUserInfo={currentUserInfo}
          onSelect={() => { localStorage.setItem('currentUserGroup', group.id.S); initiateSession(); }}
          onModal={onModal}
        />
      ))}
    </ul>
  );
}

function WorkspaceListItem({ group, isActive, currentUserInfo, onSelect, onModal }: {
  group: Group;
  isActive: boolean;
  currentUserInfo: UserInfo;
  onSelect: () => void;
  onModal: (type: string, params?: unknown[]) => void;
}) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const userRole = currentUserInfo.groups?.find((g: Group) => g.id.S === group.id.S)?.role?.S ?? 'user';
  const title = cutItemTitle(capitalize(group.title.S), 200);

  const actions: { label: string; onClick: () => void }[] = [
    { label: 'Select', onClick: () => { onSelect(); navigate('/in/folder/root'); } },
  ];
  if (userRole === 'admin' || userRole === 'pro') {
    actions.push({ label: 'Rename', onClick: () => onModal('renameWorkspace', [{ workspace: group }]) });
    actions.push({ label: 'Cover icon', onClick: () => onModal('uploadProfile', [{ workspace: group }]) });
    actions.push({ label: 'Participants', onClick: () => onModal('workspacePeopleList', [{ groupId: group.id.S }]) });
  }
  actions.push({ label: 'Exit workspace', onClick: () => onModal('exitWorkspace', [{ workspace: group }]) });
  if (userRole === 'admin') {
    actions.push({ label: 'Delete', onClick: () => onModal('deleteWorkspace', [{ workspace: group }]) });
  }

  return (
    <li className={`flex items-center gap-3 rounded-md px-2 py-2 ${isActive ? 'bg-primary-muted' : 'hover:bg-secondary'}`}>
      <Avatar name={title} src={group.image?.S} size="sm" />
      <span className="min-w-0 flex-1 truncate text-sm text-foreground">{title}</span>
      <div className="relative" ref={menuRef}>
        <button
          type="button"
          onClick={() => setMenuOpen(!menuOpen)}
          className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-muted-foreground"
        >
          <MoreHorizontal className="size-4" />
        </button>
        {menuOpen && (
          <div className="absolute right-0 z-10 mt-1 w-40 rounded-xl border border-border/60 bg-card py-1 shadow-xl">
            {actions.map((a) => (
              <button
                key={a.label}
                type="button"
                onClick={() => { a.onClick(); setMenuOpen(false); }}
                className="w-full px-3 py-1.5 text-left text-sm text-accent-foreground hover:bg-secondary"
              >
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}

// ── Workspace Modals ────────────────────────────────────────────────

function WorkspaceModals({ modalOpen, modalParams, onClose, ...props }: {
  modalOpen: string | undefined;
  modalParams: unknown[] | undefined;
  onClose: () => void;
  updateIsQuerying: (q: boolean) => void;
  currentUserInfo: UserInfo;
  refreshList: (...args: unknown[]) => void;
  currentUserGroup: string;
  groupsList: Group[];
  getWorkspacePeopleList?: () => void;
  updateCurrentUserInfo?: () => void;
  roleInCurrentWorkspace: string;
  packageType: string;
}) {
  if (!modalOpen) return null;
  const params = (modalParams?.[0] ?? {}) as Record<string, unknown>;

  const titles: Record<string, string> = {
    uploadProfile: 'Workspace icon',
    createWorkspace: 'Create workspace',
    workspacePeopleList: 'Participants',
    exitWorkspace: 'Exit workspace',
    renameWorkspace: 'Rename workspace',
    deleteWorkspace: 'Delete workspace',
  };

  return (
    <Modal open onClose={onClose} title={titles[modalOpen] || ''} size="md">
      {modalOpen === 'uploadProfile' && (
        <SetPictureModal
          currentUserInfo={params.workspace ?? props.currentUserInfo}
          closeModal={onClose}
          useCase="workspace"
          updateCurrentUserInfo={props.updateCurrentUserInfo}
          updateIsQuerying={props.updateIsQuerying}
          refreshList={props.refreshList}
          getWorkspacePeopleList={props.getWorkspacePeopleList}
        />
      )}
      {modalOpen === 'createWorkspace' && (
        <CreateWorkspaceModal
          closeModal={onClose}
          updateIsQuerying={props.updateIsQuerying}
          refreshList={props.refreshList}
          currentUserGroup={props.currentUserGroup}
          currentUserInfo={props.currentUserInfo}
          groupsList={props.groupsList}
          useCase={params.useCase}
          roleInCurrentWorkspace={props.roleInCurrentWorkspace}
          packageType={props.packageType}
        />
      )}
      {modalOpen === 'workspacePeopleList' && (
        <WorkspaceParticipiantsModal
          closeModal={onClose}
          updateIsQuerying={props.updateIsQuerying}
          currentUserInfo={props.currentUserInfo}
          groupId={params.groupId ?? props.currentUserGroup}
          getWorkspacePeopleList={props.getWorkspacePeopleList}
          refreshList={props.refreshList}
        />
      )}
      {modalOpen === 'exitWorkspace' && (
        <ExitWorkspaceModal
          closeModal={onClose}
          updateIsQuerying={props.updateIsQuerying}
          workspace={params.workspace}
          refreshList={props.refreshList}
        />
      )}
      {modalOpen === 'renameWorkspace' && (
        <RenameModal
          closeModal={onClose}
          updateIsQuerying={props.updateIsQuerying}
          workspace={params.workspace}
          refreshList={props.refreshList}
          title="Rename workspace"
          placeholder="New name"
        />
      )}
      {modalOpen === 'deleteWorkspace' && (
        <DeleteItemModal
          closeModal={onClose}
          updateIsQuerying={props.updateIsQuerying}
          selectedItems={params.workspace ? [params.workspace] : []}
          userIsAdmin={props.roleInCurrentWorkspace === 'admin'}
          refreshList={props.refreshList}
        />
      )}
    </Modal>
  );
}
